import { CalendarDays, Layers } from 'lucide-react';

import { noticeTimeAgo, type NoticeType } from '@/features/teacher/types/notices';
import { formatInstituteDate } from '@/features/teacher/utils';
import { cn } from '@/lib/utils';
import { getNoticeTheme } from './noticeTheme';

export interface CompactNoticeCardProps {
  notice: {
    title: string;
    /** Empty when the notice has none. */
    body: string;
    noticeType: NoticeType;
    /** `YYYY-MM-DD` — the day the notice is about. */
    noticeDate: string;
    /** ISO instant it went up; adds a "put up 2 days ago" hint when known. */
    postedAt?: string;
  };
  /** Who it was sent to — "Class 9 - A". Left off when the reader already knows. */
  audience?: string;
}

/**
 * One notice as a dashboard preview line: the kind, the date and the title,
 * with the body cut to two lines. The full notice lives on the notices page.
 *
 * Laid out like the student overview's notice line, so a family reading on
 * either panel sees the same card. The kind's icon, label and colours come from
 * `getNoticeTheme`, the one map every notice board draws from.
 */
export function CompactNoticeCard({ notice, audience }: CompactNoticeCardProps) {
  const theme = getNoticeTheme(notice.noticeType);

  return (
    <article className="flex items-start gap-3 rounded-xl border border-line bg-surface p-3.5 transition-shadow hover:shadow-sm">
      <span
        className={cn(
          'mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl border',
          theme.iconBg,
          theme.iconBorder,
          theme.iconText,
        )}
        aria-hidden
      >
        <theme.Icon className="h-4 w-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'w-fit rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider',
              theme.badge,
            )}
          >
            {theme.label}
          </span>
          <span
            className="flex shrink-0 items-center gap-1 text-xs font-medium tabular-nums text-ink-3"
            title={notice.postedAt ? `Put up ${noticeTimeAgo(notice.postedAt)}` : undefined}
          >
            <CalendarDays className="h-3.5 w-3.5" aria-hidden />
            {formatInstituteDate(notice.noticeDate)}
          </span>
        </div>

        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-ink">{notice.title}</h3>

        {notice.body && (
          <p className="line-clamp-2 whitespace-pre-line text-xs font-medium leading-relaxed text-ink-2">
            {notice.body}
          </p>
        )}

        {audience && (
          <span className="flex items-center gap-1 text-xs font-medium text-ink-3">
            <Layers className="h-3 w-3" aria-hidden />
            <span className="truncate">{audience}</span>
          </span>
        )}
      </div>
    </article>
  );
}
